import { useEffect, useState } from "react";

import Space from "antd/es/space/index";
import Form from "antd/es/form/index";

import * as RequestCar from "../../utils/requestApiCar";

import * as Common from "../../commons/common";
import * as TypeDTO from "../../commons/typeDTO";
import * as TypeUtils from "../../commons/typeUtils";

import CustomForm from "./CustomForm";

import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";

const SohEstimated = ({ carId }: { carId?: number }) => {
    const [form] = Form.useForm();

    const [sohEstimateds, setSohEstimateds] = useState<TypeDTO.SohEstimationDto[]>([]);
    const [series, setSeries] = useState<TypeUtils.ChartData[]>([]);

    const contents: Array<TypeUtils.formType> = [{ name: "lastSoh", label: "SOH", span: 24, required: true }];

    const { requestApiSohEstimation, resultApiSohEstimation } = RequestCar.useRequestApiSohEstimation();

    useEffect(() => {
        carId && requestApiSohEstimation(carId);
    }, [carId]);

    useEffect(() => {
        if (!resultApiSohEstimation) return;
        setSohEstimateds(resultApiSohEstimation.sohEstimations);
    }, [resultApiSohEstimation]);

    useEffect(() => {
        if (sohEstimateds.length > 0) {
            const carInfo = sohEstimateds[0].car;
            let mileageTitle = "누적 충/방전량";
            if (carInfo?.makerModel?.makerModelName === "LIONNE") {
                mileageTitle = "총 주행거리(" + Common.UNIT_DISTANCE + ")";
            }

            const estimatedDate = sohEstimateds[0].estimatedDate && dayjs(sohEstimateds[0].estimatedDate).add(-1, "day").format(Common.FORMAT_DATE);
            const lastSoh = `${Math.min((sohEstimateds[0].estimatedCap / sohEstimateds[0].initCap) * 100, 100).toFixed(2)}% (${estimatedDate} 기준)`;
            form.setFieldsValue({ lastSoh: lastSoh });

            const Fa = sohEstimateds[0].fa;
            const Fb = sohEstimateds[0].fb;
            const Fc = sohEstimateds[0].fc;

            const seriesList = sohEstimateds
                .map((element) => {
                    if (element.estimatedCap === null) return null;
                    const estimatedCap = Fa * (1 - (Fb / 100) * element.throughtput ** Fc);

                    return {
                        x: dayjs(element.estimatedDate).add(-1, "day").format("YYYY-MM"),
                        estimatedSoh: Math.min((estimatedCap / element.initCap) * 100, 100),
                        mileage: element.mileage,
                        chargingCount: element.chargingCount,
                    };
                })
                .filter((item) => item !== null);

            setSeries([
                {
                    name: "SOH",
                    type: "line",
                    data: seriesList
                        .map((item) => {
                            return {
                                x: item.x,
                                y: Number(Number(item.estimatedSoh).toFixed(2)),
                            };
                        })
                        .reverse(),
                },
                {
                    name: mileageTitle,
                    type: "line",
                    data: seriesList
                        .map((item) => {
                            return {
                                x: item.x,
                                y: Number(Number(item.mileage).toFixed(2)),
                            };
                        })
                        .reverse(),
                },
                {
                    name: "충전 횟수",
                    type: "bar",
                    data: seriesList
                        .map((item) => {
                            return {
                                x: item.x,
                                y: Number(item.chargingCount).toFixed(0),
                            };
                        })
                        .reverse(),
                },
            ]);

            const options = {
                ...defaultOptions,
                dataLabels: {
                    ...defaultOptions.dataLabels,
                    formatter: function (val: number, { seriesIndex }: any) {
                        return val.toFixed(seriesIndex === 0 ? 1 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
                yaxis: [
                    {
                        min: 0,
                        max: 100,
                        tickAmount: 5,
                        title: {
                            text: "SOH(" + Common.UNIT_PERCENT + ")",
                            style: {
                                color: "#0A84FF",
                            },
                        },
                        axisTicks: {
                            show: true,
                        },
                        axisBorder: {
                            show: true,
                            color: "#0A84FF",
                        },
                        labels: {
                            style: {
                                colors: "#0A84FF",
                            },
                            formatter: function (val: number) {
                                return val?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                            },
                        },
                    },
                    {
                        opposite: true,
                        axisBorder: {
                            show: true,
                            color: "#5E5CE6",
                        },
                        title: {
                            text: mileageTitle,
                            style: {
                                color: "#5E5CE6",
                            },
                        },
                        labels: {
                            style: {
                                colors: "#5E5CE6",
                            },
                            formatter: function (val: number) {
                                return val?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                            },
                        },
                    },
                    {
                        opposite: true,
                        axisBorder: {
                            show: true,
                            color: "#BF5AF2",
                        },
                        title: {
                            text: "충전 횟수(회)",
                            style: {
                                color: "#BF5AF2",
                            },
                        },
                        labels: {
                            style: {
                                colors: "#BF5AF2",
                            },
                            formatter: function (val: number) {
                                return val?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                            },
                        },
                    },
                ],
                tooltip: {
                    ...defaultOptions.tooltip,
                    y: {
                        formatter: function (value: number, { series, seriesIndex, dataPointIndex, w }: any) {
                            return (
                                value?.toFixed(seriesIndex === 0 ? 1 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
                                (seriesIndex === 0 ? Common.UNIT_PERCENT : "") +
                                (seriesIndex === 1 ? Common.UNIT_DISTANCE : "") +
                                (seriesIndex === 2 ? "회" : "")
                            );
                        },
                    },
                },
            };

            setOption(options);
        }
    }, [sohEstimateds]);

    const defaultOptions = {
        colors: ["#0A84FF", "#5E5CE6", "#BF5AF2", "#FF2D55", "#FF453A", "#FF9F0A", "#FFD60A", "#32D74B", "#64D2FF"],
        chart: {
            height: "350px",
            type: "line",
            toolbar: {
                show: true,
                tools: {
                    download: false,
                    selection: false,
                    pan: false,
                    zoomin: true,
                    zoomout: true,
                },
                offsetY: -4,
            },
        },
        stroke: {
            show: true,
            width: [2, 3, 0],
            colors: ["#0A84FF", "#5E5CE6", "#BF5AF2"],
        },
        dataLabels: {
            enabled: true,
            formatter: function (val: number, { seriesIndex }: any) {
                return val.toFixed(2) + (seriesIndex === 0 ? "%" : "km");
            },
            style: {
                fontSize: "12px",
                fontFamily: "Pretendard-Regular",
                colors: ["#0A84FF", "#5E5CE6", "#BF5AF2"],
            },
            background: {
                enabled: true,
                foreColor: "#fff",
                borderWidth: 0,
            },
            // offsetY: -7,
            position: "center",
        },
        plotOptions: {
            bar: {
                columnWidth: "20%",
                dataLabels: {
                    position: "bottom",
                    // total: {
                    //     enabled: true,
                    //     offsetY: -100,
                    //     style: {
                    //         colors: ["#fff"],
                    //     },
                    // },
                },
            },
        },
        title: {
            text: "SOH 추이",
            style: {
                fontFamily: "Pretendard-Bold",
                fontSize: "16px",
            },
        },
        xaxis: {
            type: "category",
        },
        yaxis: [
            {
                min: 0,
                max: 100,
                tickAmount: 5,
                title: {
                    text: "SOH(" + Common.UNIT_PERCENT + ")",
                },
                axisTicks: {
                    show: true,
                },
                axisBorder: {
                    show: true,
                },
                labels: {
                    formatter: function (val: number) {
                        return val?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                    },
                },
            },
            {
                opposite: true,
                axisBorder: {
                    show: true,
                },
                title: {
                    text: "총 주행거리(" + Common.UNIT_DISTANCE + ")",
                },
            },
            {
                opposite: true,
                axisBorder: {
                    show: true,
                },
                title: {
                    text: "충전 횟수(회)",
                },
            },
        ],
        tooltip: {
            enable: true,
            shared: true,
            marker: {
                show: false,
            },
            x: {
                show: false,
            },
            y: {
                formatter: function (value: number) {
                    return value?.toFixed(2) + Common.UNIT_PERCENT;
                },
            },
        },
        legend: {
            showForSingleSeries: false,
            fontFamily: "Pretendard-Regular",
            fontSize: "14px",
        },
        markers: {
            size: 0,
        },
    };

    const [option, setOption] = useState<Record<string, any> | undefined>();

    useEffect(() => {
        setOption(defaultOptions);
    }, []);

    return resultApiSohEstimation?.sohEstimations.length > 0 ? (
        <Space size={16} direction="vertical" style={{ width: "100%" }}>
            <CustomForm form={form} type="view" contents={[{ name: "SOH", forms: contents }]} disabledBtn={true} />
            {option && (
                <ReactApexChart
                    style={{ backgroundColor: "white", overflowX: "auto", overflowY: "hidden", position: "relative" }}
                    series={series}
                    options={option}
                    type={option.chart.type}
                    height={option.chart.height}
                />
            )}
            <div style={{ textAlign: "center", fontSize: "14px", color: "var(--font2)", marginTop: "8px" }}>
                <span>SOH는 차량에서 수집된 누적 배터리 데이터를 분석하여 추정한 결과입니다. 매월 초 누적 데이터를 분석하여 SOH를 갱신합니다.</span>
            </div>
        </Space>
    ) : null;
};

export default SohEstimated;
